import React, { useCallback, useEffect, useState } from 'react'
import './PinSetup.scss'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import { TextTag } from '@enums/textTag'
import Text from '@components/Text/Text'
import { getEditProfileIdRoute } from '@constants/routes.const'
import { Params, useNavigate, useParams } from 'react-router-dom'
import { VelvetSDK } from 'sdk'
import { useUserProfile } from '@hooks/useUserProfile'
import { insertVariableOnString } from '@utils/helpers'
import { PinCodeContainer } from './PinCodeContainer/PinCodeContainer'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { useDispatch } from 'react-redux'
import {
  setParentalAction,
  setParentalControl,
  setParentalStateSnapshot,
  setProfileContentClassification,
  setProfilePIN,
} from '@actions/profileActions'
import { useTypedSelector } from 'store'
import { ParentalControlStep } from '@enums/profile.enum'

interface PinSetupProps {
  showMenu: (status: boolean) => void
}

export const PinSetup = ({ showMenu }: PinSetupProps) => {
  const V = VelvetSDK.Vocabulary.vocabulary
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { profileId } = useParams<Params>()
  const [code, setCode] = useState<string>('')
  const [shouldOutlineBeRed, setShouldOutlineBeRed] = useState(false)
  const parentalStateSnapshot = useTypedSelector((state) => state.profile.parentalStateSnapshot)
  const parentalAction = useTypedSelector((state) => state.profile.parentalAction)
  const { profileData, isLoading } = useUserProfile({
    profileId: profileId || '',
  })

  const handleCancelButton = useCallback(() => {
    if (parentalStateSnapshot) {
      dispatch(setParentalControl(parentalStateSnapshot.parentalControl))
      dispatch(setProfileContentClassification(parentalStateSnapshot.contentClassification))
      dispatch(setProfilePIN(parentalStateSnapshot.pin))
    }
    dispatch(setParentalAction(null))
    dispatch(setParentalStateSnapshot(null))
    navigate(getEditProfileIdRoute(profileId))
  }, [dispatch, navigate, parentalStateSnapshot, profileId])

  const handleSaveButton = useCallback(() => {
    if (code === profileData?.parentalControl?.parentalPIN?.toString()) {
      setShouldOutlineBeRed(true)
      return
    }
    setShouldOutlineBeRed(false)
    dispatch(setProfilePIN(code))
    dispatch(setParentalStateSnapshot(null))
    navigate(getEditProfileIdRoute(profileId))
  }, [code, dispatch, navigate, profileData?.parentalControl?.parentalPIN, profileId])

  useEffect(() => {
    showMenu(false)
  }, [showMenu])

  if (isLoading) return <DotSpinner />

  return (
    <div className="pin-setup-page">
      <div className="pin-setup-wrapper">
        <Text tag={TextTag.H1}>
          {parentalAction === ParentalControlStep.CHANGE_PIN ? V.editDigitPIN : V.createDigitPIN}
        </Text>
        <Text tag={TextTag.P}>
          {insertVariableOnString(V.createDigitPINInfo, {
            name: profileData?.name ?? '',
          })}
        </Text>
        <PinCodeContainer setCode={setCode} shouldOutlineBeRed={shouldOutlineBeRed} />
        <div className="buttons">
          <SecondaryButton disabled={code.length !== 4} onClick={handleSaveButton}>
            {V.editProfileSaveButtonLabel}
          </SecondaryButton>
          <SecondaryButton onClick={handleCancelButton}>{V.genericCancelLabel}</SecondaryButton>
        </div>
      </div>
    </div>
  )
}
